/**
 * MapLoadingToast.tsx
 * مؤشّر تحميل علوي (أسلوب Zoom Earth) يظهر **فقط عند تبديل النموذج أو الطبقة** — لا عند
 * تغيّر الوقت أثناء التشغيل (كي لا يومض كل خطوة). يكشف الجهوزية بتحميل صورة الإطار الحالي
 * للطبقة الفعّالة (يشارك طلب المتصفّح نفسه، فإن كانت مخزّنة يختفي فوراً بلا وميض).
 */
import { useEffect, useRef, useState } from 'react';
import { useWeatherStore } from '../../store/weatherStore';
import { layerFrameUrl, whenRasterVersionKnown } from '../../services/rasterVersion';
import { loadRasterImage } from '../../services/rasterImageCache';
import type { ForecastGridType } from '../../config/weatherLayers';
import type { WeatherModelId } from '../../store/types';
import { useTranslation } from '../../i18n/LanguageContext';

interface Props {
    rasterDir: string;
    activeType: ForecastGridType | null;
    windVisible: boolean;
    modelName: WeatherModelId;
}

export function MapLoadingToast({ rasterDir, activeType, windVisible, modelName }: Props) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [label, setLabel] = useState('');
    const prevModel = useRef(modelName);

    // يعمل فقط عند تغيّر النموذج (rasterDir) أو الطبقة (activeType/wind) — لا الوقت.
    useEffect(() => {
        const type = activeType ?? (windVisible ? 'wind' : null);
        if (!type) { setLoading(false); return; }

        const idx = useWeatherStore.getState().currentTimeIndex;

        const modelSwitched = prevModel.current !== modelName;
        prevModel.current = modelName;
        setLabel(modelSwitched
            ? t('loading.model', { name: modelName })
            : t('loading.layer', { name: t(`loading.layerShort.${type}`) }));

        let cancelled = false;
        // نُظهر المؤشّر فوراً كإشارة استجابة بصرية — حتى لو كانت الصورة مخزّنة، يبقى
        // ظاهراً لمدّة دنيا قصيرة (بدل الاختفاء الفوري بلا وميض) كي يشعر المستخدم بتغيّر حقيقي.
        setLoading(true);
        const MIN_VISIBLE_MS = 700;
        const shownAt = performance.now();
        const hide = () => {
            if (cancelled) return;
            const remaining = MIN_VISIBLE_MS - (performance.now() - shownAt);
            if (remaining > 0) setTimeout(() => { if (!cancelled) setLoading(false); }, remaining);
            else setLoading(false);
        };

        // ننتظر رقم الدورة قبل الفحص — الرابط المُرقَّم (?v) هو نفسه الذي تحمّله الطبقة،
        // ورابط بلا ترقيم كان يُنزّل الملف مرّة إضافية ويُخزَّن يوماً كاملاً بلا فائدة.
        /*
         * **عبر كاش الراستر المشترك، لا `new Image()` مستقلّ.**
         *
         * كان المؤشّر يفتح طلبه الخاص للإطار نفسه الذي تطلبه الطبقة. لا يضمن المتصفّح
         * توحيدهما (يعتمد على توقيت الكاش ورؤوس الاستجابة)، فكان الطلب الثاني يزاحم
         * الأوّل على نطاقٍ محدود أصلاً (‏~250 ك.ب/ث لكل اتصال) — أي أن مؤشّر «جارٍ
         * التحميل» كان أحد أسباب طول التحميل الذي يبلّغ عنه.
         *
         * `loadRasterImage` يوحّد الطلبات المتزامنة للرابط نفسه ويعيد من الكاش فوراً إن
         * كان موجوداً. فالمؤشّر صار يختفي في اللحظة التي تستطيع فيها الطبقة الرسم فعلاً،
         * لا قبلها ولا بعدها، وبلا بايت واحد إضافي على الشبكة.
         */
        void whenRasterVersionKnown().then(() => {
            if (cancelled) return;
            const src = layerFrameUrl(rasterDir, type, idx);
            loadRasterImage(src).then(hide, hide);
        });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rasterDir, activeType, windVisible, modelName]);

    if (!loading) return null;
    return (
        <div
            className="absolute top-[85px] left-1/2 -translate-x-1/2 z-[950] flex items-center gap-[9px] px-4 py-[7px] text-[13px] text-white bg-[rgba(20,24,31,0.82)] border border-white/[0.14] rounded-full backdrop-blur-[8px] shadow-[0_4px_18px_rgba(0,0,0,0.3)] pointer-events-none whitespace-nowrap"
            role="status"
            aria-live="polite"
        >
            <span
                className="w-[14px] h-[14px] border-2 border-white/30 border-t-white rounded-full animate-spin-360 [animation-duration:0.7s] shrink-0"
                aria-hidden="true"
            />
            <span>{label}…</span>
        </div>
    );
}
